<script setup lang="ts">
// 面包屑单项数据类型
type BreadcrumbItem = {
  label: string
  route?: string | Record<string, unknown>
  color?: string
}

const { localePath } = useAppI18n()

/** 面包屑数据由父组件传入，有 route 时渲染为可跳转链接。 */
const props = withDefaults(
  defineProps<{
    items?: BreadcrumbItem[]
  }>(),
  {
    items: () => [],
  },
)

/** 统一兜底为数组，避免模板层重复判空。 */
const breadcrumbItems = computed(() => props.items ?? [])

/** 默认激活项颜色。 */
const activeColor = '#292A2E'

/** 默认非激活项颜色。 */
const inactiveColor = '#7d818a'

/**
 * 判断面包屑目标是否为站外地址，站内路由交给 Nuxt 客户端导航处理。
 * @param route 面包屑跳转目标
 * @returns 是否需要使用 external 模式
 */
const isExternalRoute = (route: BreadcrumbItem['route']) => typeof route === 'string' && /^(?:https?:)?\/\//.test(route)

/**
 * 计算面包屑项颜色，优先使用父组件传入的单项颜色。
 * @param item 面包屑单项数据
 * @param isLink 当前项是否渲染为链接
 * @returns 最终展示色值
 */
const getBreadcrumbItemColor = (item: BreadcrumbItem, isLink: boolean) =>
  item.color ?? (isLink ? inactiveColor : activeColor)
</script>

<template>
  <nav v-if="breadcrumbItems.length" aria-label="Breadcrumb" class="flex w-full items-center gap-[4px] py-[24px]">
    <template v-for="(item, index) in breadcrumbItems" :key="`${item.label}-${item.route || index}`">
      <NuxtLink
        v-if="item.route"
        :to="localePath(item.route)"
        :external="isExternalRoute(item.route)"
        :style="{
          color: getBreadcrumbItemColor(item, true),
        }"
        class="text-[14px] leading-[22px] font-[400] no-underline transition-opacity hover:opacity-80"
      >
        {{ item.label }}
      </NuxtLink>

      <span
        v-else
        aria-current="page"
        :style="{
          color: getBreadcrumbItemColor(item, false),
        }"
        class="text-[14px] leading-[22px] font-[400]"
      >
        {{ item.label }}
      </span>

      <svg
        v-if="index < breadcrumbItems.length - 1"
        aria-hidden="true"
        :style="{
          color: getBreadcrumbItemColor(item, Boolean(item.route)),
        }"
        class="size-[14px] shrink-0 rtl:rotate-180"
        viewBox="0 0 14 14"
        fill="none"
      >
        <path d="M7.684 7 4.796 4.112 5.621 3.288 9.333 7 5.621 10.712 4.796 9.887 7.684 7Z" fill="currentColor" />
      </svg>
    </template>
  </nav>
</template>
